import React, { useState, useEffect } from 'react';
import { captureCanvas } from '../utils/pdfExport';
import { Eye, Download, X } from 'lucide-react';

const PDFPreview = ({ isOpen, onClose, config, onDownloadPDF }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      generatePreview();
    }
  }, [isOpen]);

  const generatePreview = async () => {
    setIsGenerating(true);
    setError(null);
    
    try {
      const canvas = document.querySelector('canvas');
      if (!canvas) {
        throw new Error('Canvas non trovato');
      }
      
      const imageDataUrl = await captureCanvas(canvas);
      setPreviewImage(imageDataUrl);
    } catch (err) {
      console.error('Preview generation error:', err);
      setError('Errore nella generazione dell\'anteprima');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    onDownloadPDF();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-800">
            Anteprima PDF
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Preview Image */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">
                Anteprima 3D
              </h3>
              
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                {isGenerating ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-gray-600 text-sm">Generando anteprima...</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="h-64 flex items-center justify-center">
                    <div className="text-center text-red-600">
                      <p className="text-sm">{error}</p>
                      <button
                        onClick={generatePreview}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Riprova
                      </button>
                    </div>
                  </div>
                ) : previewImage ? (
                  <img
                    src={previewImage}
                    alt="Anteprima 3D"
                    className="w-full h-auto"
                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                  />
                ) : (
                  <div className="h-64 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Eye size={24} className="mx-auto mb-2" />
                      <p className="text-sm">Nessuna anteprima disponibile</p>
                    </div>
                  </div>
                )}
              </div>
              
              {previewImage && !isGenerating && (
                <p className="text-xs text-gray-500">
                  Questa immagine sarà inclusa nel PDF insieme alla configurazione
                </p>
              )}
            </div>

            {/* Configuration Summary */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-700">
                Contenuto PDF
              </h3>
              
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="space-y-3 text-sm">
                  <div className="flex items-center text-green-600">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                    Header con logo TAAPP
                  </div>
                  <div className="flex items-center text-green-600">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                    Data di generazione
                  </div>
                  <div className="flex items-center text-green-600">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                    Anteprima 3D della configurazione
                  </div>
                  <div className="flex items-center text-green-600">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                    Dettagli completi della configurazione
                  </div>
                  <div className="flex items-center text-green-600">
                    <div className="w-2 h-2 bg-green-600 rounded-full mr-2"></div>
                    Informazioni di contatto
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">
                  Dimensioni:
                </h4>
                <p className="text-blue-700 text-sm">
                  {config.width}m × {config.length}m × {config.height}m
                </p>
                <p className="text-blue-700 text-sm">
                  Area: {Math.round(config.width * config.length)} m²
                </p>
                <p className="text-blue-700 text-sm">
                  Moduli: {config.modules}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              Annulla
            </button>
            <button
              onClick={handleDownload}
              disabled={isGenerating}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-medium"
            >
              <Download size={16} className="mr-2" />
              Scarica PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFPreview;